import type { MouseEvent } from 'react';
import type { LoopState } from '../../../apps/apis/commandCenter/types';
import { LOOP_STATE_LABELS } from '../domain/loopBoard';
import type { AgentCard as AgentCardModel } from '../types';

export interface LoopControlsProps {
  card: AgentCardModel;
  /** True when the tile already carries a runtime, which is what Connect and Unlink switch on. */
  wired: boolean;
  onLoopTransition?: ((loopId: string, toState: LoopState) => void) | undefined;
  onLoopRemove?: ((loopId: string) => void) | undefined;
  onLoopConnect?: ((loopId: string) => void) | undefined;
  onLoopDisconnect?: ((loopId: string) => void) | undefined;
}

/**
 * What a person can do to a loop, in one place. The card and the drill-down both render this: when the
 * card carried the controls alone, opening a loop took away the only actions it had.
 * Every interaction stops propagating — on a card the whole tile is a button, and picking a state must
 * not also open the drill-down.
 */
const LoopControls = ({
  card,
  wired,
  onLoopTransition,
  onLoopRemove,
  onLoopConnect,
  onLoopDisconnect,
}: LoopControlsProps) => {
  const loop = card.loop;
  if (!loop) {
    return null;
  }
  const stop = (e: MouseEvent, run: () => void) => {
    e.stopPropagation();
    run();
  };

  return (
    <div
      className="a-loop"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="presentation"
    >
      <select
        className="inp"
        aria-label={`Move ${card.name}`}
        data-testid={`loop-move-${loop.loopId}`}
        value=""
        disabled={loop.nextStates.length === 0}
        onChange={(e) => {
          const next = e.target.value as LoopState;
          if (next && onLoopTransition) {
            onLoopTransition(loop.loopId, next);
          }
        }}
      >
        <option value="">Move to…</option>
        {loop.nextStates.map((state) => (
          <option key={state} value={state}>
            {LOOP_STATE_LABELS[state]}
          </option>
        ))}
      </select>
      {/* An idea has not been built yet, so there is nothing for it to name. */}
      {!wired && loop.state !== 'idea' ? (
        <button
          type="button"
          className="btn-quiet"
          data-testid={`loop-connect-${loop.loopId}`}
          onClick={(e) => stop(e, () => onLoopConnect?.(loop.loopId))}
        >
          Connect…
        </button>
      ) : null}
      {wired ? (
        <button
          type="button"
          className="btn-quiet"
          data-testid={`loop-unlink-${loop.loopId}`}
          onClick={(e) => stop(e, () => onLoopDisconnect?.(loop.loopId))}
        >
          Unlink
        </button>
      ) : null}
      {loop.canRemove ? (
        <button
          type="button"
          className="btn-quiet"
          data-testid={`loop-remove-${loop.loopId}`}
          onClick={(e) => stop(e, () => onLoopRemove?.(loop.loopId))}
        >
          Remove
        </button>
      ) : null}
    </div>
  );
};

export default LoopControls;
